(() => {
  const ROOT_SELECTOR = '[data-bundle-builder]';
  const IMAGE_SELECTOR = '[data-bundle-product] .bn-bundle-card__media img';
  const TRIGGER_ATTRIBUTE = 'data-bn-bundle-lightbox-trigger';
  const MAX_SCALE = 4;

  let overlay = null;
  let overlayImage = null;
  let overlayCaption = null;
  let closeButton = null;
  let previousFocus = null;
  let lockedScrollY = 0;
  let observer = null;
  let frame = null;
  let scale = 1;
  let translateX = 0;
  let translateY = 0;
  let activePointers = new Map();
  let dragState = null;
  let pinchState = null;
  let swipeState = null;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const largestSrcsetCandidate = (image) => {
    const candidates = String(image.srcset || '')
      .split(',')
      .map((candidate) => {
        const match = candidate.trim().match(/^(.*)\s+(\d+)w$/);
        return match ? { url: match[1].trim(), width: Number(match[2]) } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.width - a.width);
    return candidates[0]?.url || '';
  };

  const enlargedSource = (image) => {
    const source = image.currentSrc || image.src || largestSrcsetCandidate(image);
    if (!source) return '';

    try {
      const url = new URL(source, window.location.href);
      if (url.hostname.includes('cdn.shopify.com') || url.searchParams.has('width')) {
        url.searchParams.set('width', '1600');
        return url.toString();
      }
    } catch (error) {
      // Keep the original source when the URL cannot be parsed.
    }

    return largestSrcsetCandidate(image) || source;
  };

  const resetTransform = () => {
    scale = 1;
    translateX = 0;
    translateY = 0;
    activePointers.clear();
    dragState = null;
    pinchState = null;
    swipeState = null;
    if (overlayImage) {
      overlayImage.style.transform = '';
    }
    overlay?.classList.remove('is-zoomed');
  };

  const clampTranslation = () => {
    if (!overlayImage || scale <= 1) {
      translateX = 0;
      translateY = 0;
      return;
    }

    const maxX = Math.max(0, (overlayImage.clientWidth * (scale - 1)) / 2);
    const maxY = Math.max(0, (overlayImage.clientHeight * (scale - 1)) / 2);
    translateX = clamp(translateX, -maxX, maxX);
    translateY = clamp(translateY, -maxY, maxY);
  };

  const applyTransform = () => {
    if (!overlayImage) return;
    clampTranslation();
    overlayImage.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
    overlay?.classList.toggle('is-zoomed', scale > 1.01);
  };

  const setScale = (nextScale) => {
    scale = clamp(nextScale, 1, MAX_SCALE);
    if (scale <= 1.01) {
      scale = 1;
      translateX = 0;
      translateY = 0;
    }
    applyTransform();
  };

  const pointerDistance = (first, second) => Math.hypot(second.x - first.x, second.y - first.y);

  const restorePageScroll = () => {
    document.body.classList.remove('bn-bundle-lightbox-open');
    document.body.style.removeProperty('top');
    window.scrollTo(0, lockedScrollY);
  };

  const closeLightbox = () => {
    if (!overlay || overlay.hidden) return;

    overlay.hidden = true;
    overlay.removeAttribute('data-open');
    overlayImage.removeAttribute('src');
    overlayImage.alt = '';
    overlayCaption.textContent = '';
    resetTransform();
    restorePageScroll();

    const focusTarget = previousFocus;
    previousFocus = null;
    if (focusTarget instanceof HTMLElement && document.contains(focusTarget)) {
      focusTarget.focus({ preventScroll: true });
    }
  };

  const startPinch = () => {
    const pointers = Array.from(activePointers.values());
    if (pointers.length !== 2) {
      pinchState = null;
      return;
    }
    pinchState = {
      distance: pointerDistance(pointers[0], pointers[1]),
      scale,
    };
    dragState = null;
    swipeState = null;
  };

  const createOverlay = () => {
    if (overlay) return;

    overlay = document.createElement('div');
    overlay.className = 'bn-bundle-lightbox';
    overlay.dataset.bnBundleLightbox = '';
    overlay.hidden = true;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Imagen ampliada del producto');

    closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'bn-bundle-lightbox__close';
    closeButton.setAttribute('aria-label', 'Cerrar imagen ampliada');
    closeButton.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>';

    const content = document.createElement('div');
    content.className = 'bn-bundle-lightbox__content';

    overlayImage = document.createElement('img');
    overlayImage.className = 'bn-bundle-lightbox__image';
    overlayImage.decoding = 'async';
    overlayImage.draggable = false;

    overlayCaption = document.createElement('p');
    overlayCaption.className = 'bn-bundle-lightbox__caption';

    content.append(overlayImage, overlayCaption);
    overlay.append(closeButton, content);
    document.body.appendChild(overlay);

    closeButton.addEventListener('click', closeLightbox);
    content.addEventListener('click', (event) => event.stopPropagation());
    overlay.addEventListener('click', closeLightbox);

    overlayImage.addEventListener('load', () => {
      resetTransform();
      applyTransform();
    });

    overlayImage.addEventListener('dblclick', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setScale(scale > 1 ? 1 : 2);
    });

    overlayImage.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      overlayImage.setPointerCapture?.(event.pointerId);
      activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (activePointers.size === 2) {
        startPinch();
        return;
      }

      if (scale > 1.01) {
        dragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          translateX,
          translateY,
        };
      } else if (event.pointerType === 'touch') {
        swipeState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
        };
      }
    });

    overlayImage.addEventListener('pointermove', (event) => {
      if (!activePointers.has(event.pointerId)) return;
      event.preventDefault();
      activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (activePointers.size === 2) {
        const pointers = Array.from(activePointers.values());
        if (!pinchState) startPinch();
        if (!pinchState || pinchState.distance <= 0) return;
        const ratio = pointerDistance(pointers[0], pointers[1]) / pinchState.distance;
        setScale(pinchState.scale * ratio);
        return;
      }

      if (dragState?.pointerId === event.pointerId && scale > 1.01) {
        translateX = dragState.translateX + (event.clientX - dragState.startX);
        translateY = dragState.translateY + (event.clientY - dragState.startY);
        applyTransform();
      }
    });

    const finishPointer = (event) => {
      const currentSwipe = swipeState?.pointerId === event.pointerId ? swipeState : null;
      activePointers.delete(event.pointerId);
      overlayImage.releasePointerCapture?.(event.pointerId);

      if (activePointers.size === 1 && scale > 1.01) {
        const [pointerId, point] = activePointers.entries().next().value;
        dragState = {
          pointerId,
          startX: point.x,
          startY: point.y,
          translateX,
          translateY,
        };
      } else if (activePointers.size === 2) {
        startPinch();
      } else if (activePointers.size === 0) {
        dragState = null;
        pinchState = null;
      }

      if (currentSwipe && scale === 1) {
        const deltaX = event.clientX - currentSwipe.startX;
        const deltaY = event.clientY - currentSwipe.startY;
        if (deltaY > 90 && Math.abs(deltaX) < 70) {
          closeLightbox();
        }
      }
      if (swipeState?.pointerId === event.pointerId) swipeState = null;
    };

    overlayImage.addEventListener('pointerup', finishPointer);
    overlayImage.addEventListener('pointercancel', finishPointer);

    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeLightbox();
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        closeButton.focus();
      }
    });
  };

  const openLightbox = (trigger) => {
    createOverlay();

    const card = trigger.closest('[data-bundle-product]');
    const title = String(card?.dataset.productTitle || trigger.alt || 'Producto').trim();
    const source = enlargedSource(trigger);
    if (!source) return;

    previousFocus = trigger;
    lockedScrollY = window.scrollY;
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.classList.add('bn-bundle-lightbox-open');

    resetTransform();
    overlayImage.src = source;
    overlayImage.alt = title;
    overlayCaption.textContent = title;
    overlay.hidden = false;
    overlay.dataset.open = 'true';
    closeButton.focus({ preventScroll: true });
  };

  const enhanceImage = (image) => {
    if (image.hasAttribute(TRIGGER_ATTRIBUTE)) return;

    const media = image.closest('.bn-bundle-card__media');
    const card = image.closest('[data-bundle-product]');
    if (!media || !card) return;

    const title = String(card.dataset.productTitle || image.alt || 'producto').trim();
    image.setAttribute(TRIGGER_ATTRIBUTE, '');
    image.setAttribute('role', 'button');
    image.setAttribute('tabindex', '0');
    image.setAttribute('aria-label', `Ampliar imagen de ${title}`);
    image.draggable = false;
    media.classList.add('bn-bundle-card__media--zoomable');

    if (!media.querySelector('[data-bn-bundle-zoom-cue]')) {
      const cue = document.createElement('span');
      cue.className = 'bn-bundle-card__zoom-cue';
      cue.dataset.bnBundleZoomCue = '';
      cue.setAttribute('aria-hidden', 'true');
      cue.innerHTML = '<svg viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="5.5"/><path d="M15 15l5 5M10.5 7.5v6M7.5 10.5h6"/></svg>';
      media.appendChild(cue);
    }
  };

  const refresh = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) {
      closeLightbox();
      return;
    }
    root.querySelectorAll(IMAGE_SELECTOR).forEach(enhanceImage);
  };

  const queueRefresh = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      refresh();
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.(`[${TRIGGER_ATTRIBUTE}]`);
    if (!trigger || !trigger.closest(ROOT_SELECTOR)) return;
    event.preventDefault();
    event.stopPropagation();
    openLightbox(trigger);
  }, true);

  document.addEventListener('keydown', (event) => {
    const trigger = event.target.closest?.(`[${TRIGGER_ATTRIBUTE}]`);
    if (!trigger || !trigger.closest(ROOT_SELECTOR)) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    openLightbox(trigger);
  }, true);

  const start = () => {
    createOverlay();
    refresh();
    if (!observer && document.body) {
      observer = new MutationObserver(queueRefresh);
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', queueRefresh);
  window.addEventListener('resize', () => {
    if (!overlay?.hidden) applyTransform();
  });
  document.addEventListener('shopify:section:load', queueRefresh);
})();
